import { useMemo } from 'react';
import {
  Body1Strong,
  Card,
  Table,
  TableBody,
  TableHeader,
  TableHeaderCell,
  TableRow,
  createTableColumn,
  useTableFeatures,
} from '@fluentui/react-components';
import useItemTableStyles from '@modules/items/hooks/useItemTableStyles.hook';
import type { TResolvedItemsMap } from '@modules/items/types/items.types';
import { usePersistedColumnSizing } from '@shared/hooks';
import { CHANGE_TEMPLATE_ASSESSMENTS_TABLE_ID } from '../constants';
import type { TChangeTemplateAssessment } from '../types';
import ChangeTemplateAssessmentRow from './ChangeTemplateAssessmentRow';

type TColumnId = 'number' | 'title' | 'required' | 'modified' | 'status' | 'authors';

// Match Tasks: auto-fit must not overwrite user-resized widths.
const columnSizingParams = {
  autoFitColumns: false,
  columnSizingOptions: {
    number: { defaultWidth: 64, minWidth: 48 },
    title: { defaultWidth: 320, minWidth: 160 },
    required: { defaultWidth: 110, minWidth: 90 },
    modified: { defaultWidth: 110, minWidth: 90 },
    status: { defaultWidth: 110, minWidth: 90 },
    authors: { defaultWidth: 140, minWidth: 100 },
  },
};
const columnIds: TColumnId[] = ['number', 'title', 'required', 'modified', 'status', 'authors'];

type TProps = {
  changeTemplateId: string;
  assessments: TChangeTemplateAssessment[];
  resolvedItems: TResolvedItemsMap;
  canChange: boolean;
  requiredPending: boolean;
  requiredDisabledReason: string;
  reorderDisabledReason: string;
  onReorder: (sourceId: string, targetId: string) => void;
  onRequiredChange: (assessment: TChangeTemplateAssessment, required: boolean) => void;
  onDelete: (assessment: TChangeTemplateAssessment) => void;
  templateVersion?: string;
};

const ChangeTemplateAssessmentsTable = ({
  changeTemplateId,
  assessments,
  resolvedItems,
  canChange,
  requiredPending,
  requiredDisabledReason,
  reorderDisabledReason,
  onReorder,
  onRequiredChange,
  onDelete,
  templateVersion,
}: TProps) => {
  const columns = useMemo(
    () => columnIds.map((columnId) => createTableColumn<TChangeTemplateAssessment>({ columnId })),
    []
  );
  const { columnSizing_unstable, getRows } = useTableFeatures(
    { columns, items: assessments, getRowId: (assessment) => assessment._id },
    [usePersistedColumnSizing(CHANGE_TEMPLATE_ASSESSMENTS_TABLE_ID, columnSizingParams)]
  );
  const rows = getRows();
  const tableClasses = useItemTableStyles();
  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table} {...columnSizing_unstable.getTableProps()}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('number')}>
              <Body1Strong>No.</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('title')}>
              <Body1Strong>Title</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('required')}>
              <Body1Strong>Required</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('modified')}>
              <Body1Strong>Modified</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('status')}>
              <Body1Strong>Status</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell {...columnSizing_unstable.getTableHeaderCellProps('authors')}>
              <Body1Strong>Authors</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>
        <TableBody className={tableClasses.tableBody}>
          {rows.map(({ item: assessment }, index) => (
            <ChangeTemplateAssessmentRow
              key={assessment._id}
              changeTemplateId={changeTemplateId}
              assessment={assessment}
              number={index + 1}
              getCellProps={columnSizing_unstable.getTableCellProps}
              canChange={canChange}
              requiredPending={requiredPending}
              requiredDisabledReason={requiredDisabledReason}
              reorderDisabledReason={reorderDisabledReason}
              onReorder={onReorder}
              onRequiredChange={onRequiredChange}
              onDelete={onDelete}
              document={resolvedItems[assessment.document]?.item}
              templateVersion={templateVersion}
            />
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default ChangeTemplateAssessmentsTable;
